import React from 'react';
import Modal from './Modal';

function Toggle({ checked, onChange, label, description }) {
  return (
    <button
      onClick={() => onChange(!checked)}
      className="w-full flex items-start justify-between gap-3 py-2.5 text-left group"
      role="switch"
      aria-checked={checked}
    >
      <span>
        <span className="block text-body font-medium text-ink">{label}</span>
        <span className="block text-meta text-ink-2 mt-0.5">{description}</span>
      </span>
      <span className={`mt-0.5 w-10 h-6 rounded-full p-0.5 transition flex-shrink-0 ${checked ? 'bg-accent' : 'bg-line group-hover:bg-ink-2/50'}`}>
        <span className={`block w-5 h-5 bg-surface border border-line rounded-full transition-transform ${checked ? 'translate-x-4' : ''}`} />
      </span>
    </button>
  );
}

const VOTE_MODES = [
  { id: 'cumulative', label: 'Cumulative', description: 'Unlimited stacking votes (default)' },
  { id: 'single', label: 'One per item', description: 'Each person: one up or down vote per item' },
  { id: 'budget', label: 'Vote budget', description: 'A fixed number of votes to distribute, forces prioritization' },
];

export default function SettingsModal({ settings, setSettings, onClose }) {
  return (
    <Modal title="Session settings" onClose={onClose}>
      <div className="divide-y divide-line">
        <div className="pb-4">
          <h3 className="text-meta font-semibold text-ink-2 uppercase tracking-wide mb-2">Voting mode</h3>
          <div className="space-y-1.5">
            {VOTE_MODES.map((m) => (
              <label key={m.id} className={`flex items-start gap-2.5 p-2.5 rounded-ctl border cursor-pointer transition ${settings.voteMode === m.id ? 'border-accent bg-accent-tint' : 'border-line hover:border-accent/40'}`}>
                <input
                  type="radio"
                  name="voteMode"
                  checked={settings.voteMode === m.id}
                  onChange={() => setSettings({ voteMode: m.id })}
                  className="mt-0.5 accent-accent"
                />
                <span>
                  <span className="block text-body font-medium text-ink">{m.label}</span>
                  <span className="block text-meta text-ink-2">{m.description}</span>
                </span>
              </label>
            ))}
          </div>
          {settings.voteMode === 'budget' && (
            <div className="flex items-center gap-2 mt-2.5 pl-1">
              <label className="text-meta text-ink-2" htmlFor="voteBudget">Votes per person:</label>
              <input
                id="voteBudget"
                type="number"
                min="1"
                max="100"
                value={settings.voteBudget}
                onChange={(e) => setSettings({ voteBudget: Math.max(1, Math.min(100, Number(e.target.value) || 1)) })}
                className="w-20 px-2 py-1 border border-line rounded-ctl text-body outline-none focus:ring-2 focus:border-accent focus:ring-accent/25"
              />
            </div>
          )}
        </div>

        <div className="py-2">
          <Toggle
            checked={settings.hideVotes}
            onChange={(v) => setSettings({ hideVotes: v })}
            label="Blind voting"
            description="Hide totals and ranking from students until you switch this off, prevents anchoring; switching it off is the reveal moment."
          />
          <Toggle
            checked={settings.instantAnswers}
            onChange={(v) => setSettings({ instantAnswers: v })}
            label="Instant answers"
            description="Answer each history/exam/lab entry the moment it's added, instead of waiting for you to press Reveal. Findings nobody asked for are still held back until the reveal. Good for solo practice; leave off to keep the class thinking first."
          />
          <Toggle
            checked={settings.probabilityMode}
            onChange={(v) => setSettings({ probabilityMode: v })}
            label="Probability estimates"
            description="Each student sets a 0–100% pre-test probability on diagnoses; rows rank by group median. Teaches calibration."
          />
          <Toggle
            checked={settings.phase === 'individual'}
            onChange={(v) => setSettings({ phase: v ? 'individual' : 'group' })}
            label="Individual phase (think-alone-then-share)"
            description="Students work on the normal board and grid but see only their own entries; your screen stays empty. Revealing the step (or “Merge & share”) collapses duplicates, publishes the class's combined list, and saves every student their own list of what they personally missed."
          />
        </div>
      </div>
      <p className="text-meta text-ink-2 mt-3">Settings apply live to everyone in the session.</p>
    </Modal>
  );
}
